import { Button, DialogProps } from '@mui/material';
import { useState } from 'react';

import BaseDialog from '@/components/shared/Dialog/BaseDialog';
import { useGenerateApiKey } from '@/hooks/useGenerateApiKey';

import FinishGenerateApiKeyStep from './FinishGenerateApiKeyStep';
import GenerateApiKeyStep from './GenerateApiKeyStep';

type CopyKey = 'public' | 'secret' | null;
type Step = 0 | 1 | null;

interface GenerateApiKeyDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: number;
  isRefetching: boolean;
}

function GenerateApiKeyDialog({ open, onClose, organizationId, isRefetching }: GenerateApiKeyDialogProps) {
  /* For now we provide only one external system value (hardcoded) */
  const [system, setSystem] = useState<string>('HubCatalog');
  const [publicKey, setPublicKey] = useState<string>('');
  const [secretKey, setSecretKey] = useState<string>('');
  const [activeStep, setActiveStep] = useState<Step>(0);
  const [copiedKey, setCopiedKey] = useState<CopyKey>(null);

  const { generateApiKey, isPending, error, resetError } = useGenerateApiKey();

  const handleGenerateApi = async () => {
    const result = await generateApiKey({ organizationId, system });
    setPublicKey(result.publicKey ?? '');
    setSecretKey(result.secretKey ?? '');
    setActiveStep(1);
  };

  const handleCopy = (key: CopyKey) => async () => {
    const value = key === 'public' ? publicKey : secretKey;

    try {
      if (!navigator.clipboard?.writeText) {
        setCopiedKey(null);
        return;
      }

      await navigator.clipboard.writeText(value);

      setCopiedKey(key);

      setTimeout(() => {
        setCopiedKey(null);
      }, 1500);
    } catch {
      setCopiedKey(null);
    }
  };

  const handleClose: DialogProps['onClose'] = () => {
    setActiveStep(0);
    setSystem('HubCatalog');
    setPublicKey('');
    setSecretKey('');
    setCopiedKey(null);
    resetError();
    onClose();
  };

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      title='Generate API Key'
      hasCloseButton
      footer={
        <>
          {activeStep === 0 && <Button onClick={() => handleClose({}, 'backdropClick')}>Cancel</Button>}
          {activeStep === 0 ? (
            <Button
              type='submit'
              variant='contained'
              form='external-system-form'
              loading={isPending || isRefetching}
              disabled={isPending || isRefetching}
            >
              Generate API Key
            </Button>
          ) : (
            <Button variant='contained' onClick={() => handleClose({}, 'backdropClick')}>
              Finish
            </Button>
          )}
        </>
      }
      alert={{
        isVisible: activeStep === 1,
        severity: 'warning',
        alertMessage: 'Store the secret key securely. It will not be displayed again after you close this dialog.',
      }}
    >
      {activeStep === 0 && (
        <GenerateApiKeyStep
          systemName={system}
          onSystemNameChange={(e) => {
            resetError();
            setSystem(e.target.value);
          }}
          onGenerateApiKey={handleGenerateApi}
          systemError={error}
        />
      )}
      {activeStep === 1 && (
        <FinishGenerateApiKeyStep
          publicKey={publicKey}
          secretKey={secretKey}
          isPublicKeyCopied={copiedKey === 'public'}
          onPublicKeyCopy={handleCopy('public')}
          isSecretKeyCopied={copiedKey === 'secret'}
          onSecretKeyCopy={handleCopy('secret')}
        />
      )}
    </BaseDialog>
  );
}

export default GenerateApiKeyDialog;
